import { Fragment } from 'preact'

import FormField from '@bonniernews/dn-design-system-web/assets/form-field/form-field.tsx'
import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import { IconUse } from '@bonniernews/dn-design-system-web/components/icon-sprite/icon-sprite.tsx'

export interface RadioButtonProps {
  id: string
  value: string
  label?: string
  selected?: boolean
  attributes?: {}
}

interface RadioButtonGroupProps extends SharedProps {
  buttons: RadioButtonProps[]
  name?: string
  error?: boolean
  errorMessage?: string
  required?: boolean
  stripLabel?: boolean
  disabled?: boolean
  validation?: string
  forcePx?: boolean
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/radio-button-group](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/radio-button-group)
 * - Storybook: [RadioButtonGroup](https://designsystem.dn.se/?path=/docs/basic-form-radiobuttongroup--docs)
 *
 * This component is used to render a group of radio buttons.
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/radio-button-group/radio-button-group.scss`
 */
export const RadioButtonGroup = ({
  buttons,
  error,
  name,
  errorMessage,
  required,
  stripLabel,
  disabled,
  forcePx,
  classNames,
}: RadioButtonGroupProps) => {
  const radioButtonClassName = 'ds-radio-btn'
  const wrapperClasses = formatClassString(['ds-radio-btn-group', forcePx && 'ds-force-px', classNames])

  return (
    <FormField error={error} errorMessage={errorMessage} wrapperClasses={wrapperClasses}>
      {buttons?.map((button) => {
        const { id, selected, value, label, attributes } = button

        return (
          <Fragment key={id}>
            <input
              type='radio'
              id={id}
              name={name}
              defaultChecked={selected}
              disabled={disabled}
              required={required}
              value={value}
              {...attributes}
            />

            {stripLabel ? (
              <div className={`${radioButtonClassName}__inner`}>
                <RadioButtonIcons radioButtonClassName={radioButtonClassName} />
              </div>
            ) : (
              <label className={`${radioButtonClassName}__inner`} htmlFor={id}>
                <RadioButtonIcons radioButtonClassName={radioButtonClassName} />
                {label && <span className={`${radioButtonClassName}__text`}>{label}</span>}
              </label>
            )}
          </Fragment>
        )
      })}
    </FormField>
  )
}

const RadioButtonIcons = ({ radioButtonClassName }: { radioButtonClassName: string }) => {
  return (
    <span className={`${radioButtonClassName}__icon`}>
      <IconUse iconName='radio_button_unchecked' />
      <IconUse iconName='radio_button_checked' />
    </span>
  )
}
